import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface ProductVariantCard {
    key: string;
    deliveryWindowLabel: string;
    colorName: string;
    imgUrl: string;
    isSoldOut: boolean;
    isActive: boolean;
}

interface ProductVariantStripProps {
    variants: ProductVariantCard[];
    onSelect: (key: string) => void;
    onMouseDown?: (e: React.MouseEvent) => void;
    /** Switch to horizontal carousel when variant count exceeds this (default 1 = always carousel). */
    carouselThreshold?: number;
}

const DEFAULT_CAROUSEL_THRESHOLD = 1;

export const ProductVariantStrip: React.FC<ProductVariantStripProps> = ({
    variants,
    onSelect,
    onMouseDown,
    carouselThreshold = DEFAULT_CAROUSEL_THRESHOLD,
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const isCarousel = variants.length > carouselThreshold;

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el || !isCarousel) {
            setCanScrollPrev(false);
            setCanScrollNext(false);
            return;
        }
        const maxScroll = el.scrollWidth - el.clientWidth;
        setCanScrollPrev(el.scrollLeft > 2);
        setCanScrollNext(el.scrollLeft < maxScroll - 2);
    }, [isCarousel]);

    const scrollActiveIntoView = useCallback(
        (behavior: ScrollBehavior = 'smooth') => {
            const el = scrollRef.current;
            if (!el || !isCarousel) {
                return;
            }

            const active = el.querySelector(
                '[data-variant-key].po__variants-grid__card--active'
            ) as HTMLElement | null;
            if (!active) {
                return;
            }

            const relativeLeft =
                active.getBoundingClientRect().left -
                el.getBoundingClientRect().left +
                el.scrollLeft;
            const targetLeft =
                relativeLeft - (el.clientWidth - active.clientWidth) / 2;
            const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);

            el.scrollTo({
                left: Math.min(maxScroll, Math.max(0, targetLeft)),
                behavior,
            });
            updateScrollState();
        },
        [isCarousel, updateScrollState]
    );

    useEffect(() => {
        updateScrollState();
    }, [variants, updateScrollState]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el || !isCarousel) {
            return;
        }

        const onScroll = () => updateScrollState();
        el.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);

        return () => {
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [isCarousel, updateScrollState]);

    useEffect(() => {
        if (!isCarousel) {
            return;
        }
        requestAnimationFrame(() => {
            scrollActiveIntoView('auto');
        });
    }, [isCarousel, variants, scrollActiveIntoView]);

    const scrollBy = (direction: -1 | 1) => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }
        el.scrollBy({
            left: direction * Math.max(140, el.clientWidth * 0.55),
            behavior: 'smooth',
        });
    };

    const renderCard = (variant: ProductVariantCard) => (
        <button
            type="button"
            key={variant.key}
            data-variant-key={variant.key}
            className={
                'po__variants-grid__card' +
                (variant.isActive ? ' po__variants-grid__card--active' : '')
            }
            onMouseDown={onMouseDown}
            onClick={() => onSelect(variant.key)}
        >
            <p className="po__variants-grid__card__meta">{variant.deliveryWindowLabel}</p>
            <div className="po__variants-grid__card__image">
                <img src={encodeURI(variant.imgUrl)} alt={variant.colorName} />
                {variant.isSoldOut && (
                    <span className="po__variants-grid__card__sold-out">Sold Out</span>
                )}
            </div>
            <p className="po__variants-grid__card__name">{variant.colorName}</p>
        </button>
    );

    const grid = (
        <div
            ref={isCarousel ? scrollRef : undefined}
            className={
                'po__variants-grid' +
                (isCarousel ? ' po__variants-grid--carousel' : '')
            }
            role="group"
            aria-label="Color variants"
        >
            {variants.map(renderCard)}
        </div>
    );

    if (!isCarousel) {
        return grid;
    }

    return (
        <div className="po__variants-carousel">
            <button
                type="button"
                className={
                    'po__variants-carousel__nav po__variants-carousel__nav--prev' +
                    (!canScrollPrev ? ' po__variants-carousel__nav--disabled' : '')
                }
                aria-label="Previous variants"
                disabled={!canScrollPrev}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => canScrollPrev && scrollBy(-1)}
            >
                ‹
            </button>
            {grid}
            <button
                type="button"
                className={
                    'po__variants-carousel__nav po__variants-carousel__nav--next' +
                    (!canScrollNext ? ' po__variants-carousel__nav--disabled' : '')
                }
                aria-label="Next variants"
                disabled={!canScrollNext}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => canScrollNext && scrollBy(1)}
            >
                ›
            </button>
        </div>
    );
};

export default ProductVariantStrip;
